import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Container,
  Paper,
  Grid,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Alert,
  Chip,
  CircularProgress,
  Snackbar,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { Add, Edit, Delete, Search, Warning, ImageNotSupported, Inventory, CameraAlt, PhotoLibrary } from '@mui/icons-material';
import AdminLayout from '../../components/AdminLayout';
import { collection, addDoc, getDocs, doc, deleteDoc, updateDoc, query, orderBy, getFirestore } from 'firebase/firestore';
import app from '../../config/firebase';

// Inicialización de Firestore
const db = getFirestore(app);

// Función para calcular días hasta vencimiento
const calcularDiasHastaVencimiento = (fechaVencimiento) => {
  if (!fechaVencimiento) return null;
  
  const hoy = new Date();
  const fechaVence = new Date(fechaVencimiento);
  const diferencia = fechaVence - hoy;
  return Math.ceil(diferencia / (1000 * 60 * 60 * 24));
};

const Inventario = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const fileInputRef = useRef(null);
  
  const [inventario, setInventario] = useState([]);
  const [busqueda, setBusqueda] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [itemSeleccionado, setItemSeleccionado] = useState(null);
  const [productosAlerta, setProductosAlerta] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [tomaFoto, setTomaFoto] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState({
    open: false,
    item: null
  });
  // Eliminamos la vista de tarjetas, solo usamos tabla
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [nuevoItem, setNuevoItem] = useState({
    nombre: '',
    categoria: '',
    cantidad: '',
    precio: '',
    precioVenta: '',
    proveedor: '',
    fechaVencimiento: '',
    presentacion: '',
    imagenURL: ''
  });

  // Cargar inventario desde Firestore
  const cargarInventario = async () => {
    setCargando(true);
    try {
      const inventarioRef = collection(db, 'inventario');
      const q = query(inventarioRef, orderBy('nombre'));
      const querySnapshot = await getDocs(q);
      
      const productosData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setInventario(productosData);
      
      // Actualizar alertas de vencimiento
      const alertas = productosData.filter(item => {
        if (!item.fechaVencimiento) return false;
        const diasRestantes = calcularDiasHastaVencimiento(item.fechaVencimiento);
        return diasRestantes <= 30; // Alertar para productos que vencen en menos de 30 días
      });
      
      setProductosAlerta(alertas);
      setSnackbar({
        open: productosData.length > 0,
        message: `Se cargaron ${productosData.length} productos del inventario`,
        severity: 'info'
      });
    } catch (error) {
      console.error("Error al cargar el inventario:", error);
      setSnackbar({
        open: true,
        message: "Error al cargar el inventario: " + error.message,
        severity: 'error'
      });
    } finally {
      setCargando(false);
    }
  };
  
  // Cargar inventario al montar el componente
  useEffect(() => {
    cargarInventario();
  }, []);

  // Filtrar inventario según búsqueda
  const inventarioFiltrado = inventario.filter(item => 
    item.nombre.toLowerCase().includes(busqueda.toLowerCase()) ||
    item.categoria.toLowerCase().includes(busqueda.toLowerCase()) ||
    item.proveedor.toLowerCase().includes(busqueda.toLowerCase())
  );

  // Manejadores de eventos
  const handleBusquedaChange = (event) => {
    setBusqueda(event.target.value);
  };

  const handleOpenDialog = (item = null) => {
    if (item) {
      setItemSeleccionado(item);
      setNuevoItem(item);
    } else {
      setItemSeleccionado(null);
      setNuevoItem({
        nombre: '',
        categoria: '',
        cantidad: '',
        precio: '',
        precioVenta: '',
        proveedor: '',
        fechaVencimiento: '',
        presentacion: '',
        imagenURL: ''
      });
    }
    setTomaFoto(false); // Resetear estado de toma de foto
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setGuardando(false); // Reset loading state when closing
    setTomaFoto(false); // Reset photo capture state when closing
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setNuevoItem({
      ...nuevoItem,
      [name]: value
    });
  };

  const handleGuardarItem = async () => {
    // Prevenir múltiples clics
    if (guardando) return;
    
    setGuardando(true);
    
    try {
      // Convertir valores string a números y validar
      const cantidad = nuevoItem.cantidad === '' ? 0 : Number(nuevoItem.cantidad);
      const precio = nuevoItem.precio === '' ? 0 : Number(nuevoItem.precio);
      const precioVenta = nuevoItem.precioVenta === '' ? 0 : Number(nuevoItem.precioVenta);
      
      // Validar que no haya valores negativos
      if (cantidad < 0 || precio < 0 || precioVenta < 0) {
        setSnackbar({
          open: true,
          message: "Error: No se permiten valores negativos en cantidad o precios",
          severity: 'error'
        });
        setGuardando(false);
        return;
      }
      
      // Validar campos requeridos
      if (!nuevoItem.nombre.trim()) {
        setSnackbar({
          open: true,
          message: "Error: El nombre del producto es requerido",
          severity: 'error'
        });
        setGuardando(false);
        return;
      }
      
      if (itemSeleccionado) {
        // Editar item existente en Firestore
        const itemRef = doc(db, 'inventario', itemSeleccionado.id);
        
        // Preparar datos a actualizar (sin el campo id)
        const { id, ...datosActualizados } = nuevoItem;
        
        // Asegurar que los valores numéricos sean números
        datosActualizados.cantidad = cantidad;
        datosActualizados.precio = precio;
        datosActualizados.precioVenta = precioVenta;
        
        await updateDoc(itemRef, datosActualizados);
        
        setSnackbar({
          open: true,
          message: `Producto "${nuevoItem.nombre}" actualizado correctamente`,
          severity: 'success'
        });
      } else {
        // Agregar nuevo item en Firestore
        // Asegurar que los valores numéricos sean números
        const nuevoProducto = {
          ...nuevoItem,
          cantidad: cantidad,
          precio: precio,
          precioVenta: precioVenta,
          fechaCreacion: new Date().toISOString()
        };
        
        await addDoc(collection(db, 'inventario'), nuevoProducto);
        
        setSnackbar({
          open: true,
          message: `Producto "${nuevoItem.nombre}" agregado correctamente`,
          severity: 'success'
        });
      }
      
      // Cerrar diálogo y recargar inventario
      handleCloseDialog();
      cargarInventario();
    } catch (error) {
      console.error("Error al guardar el producto:", error);
      setSnackbar({
        open: true,
        message: `Error al ${itemSeleccionado ? 'actualizar' : 'agregar'} el producto: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setGuardando(false);
    }
  };

  const handleEliminarItem = async (id) => {
    const item = inventario.find(producto => producto.id === id);
    setConfirmDelete({
      open: true,
      item: item
    });
  };

  const handleConfirmDelete = async () => {
    if (!confirmDelete.item) return;
    
    try {
      // Eliminar item de Firestore
      await deleteDoc(doc(db, 'inventario', confirmDelete.item.id));
      
      // Actualizar UI
      setSnackbar({
        open: true,
        message: `Producto "${confirmDelete.item.nombre}" eliminado correctamente`,
        severity: 'success'
      });
      
      // Recargar inventario
      cargarInventario();
    } catch (error) {
      console.error("Error al eliminar el producto:", error);
      setSnackbar({
        open: true,
        message: "Error al eliminar el producto: " + error.message,
        severity: 'error'
      });
    } finally {
      setConfirmDelete({
        open: false,
        item: null
      });
    }
  };

  const handleCancelDelete = () => {
    setConfirmDelete({
      open: false,
      item: null
    });
  };
  
  const handleCloseSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false
    });
  };

  // Función para redimensionar y comprimir imagen
  const compressImage = (file, maxWidth = 800, maxHeight = 800, quality = 0.7) => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = () => {
        // Calcular nuevas dimensiones manteniendo proporción
        let { width, height } = img;
        
        if (width > height) {
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width = (width * maxHeight) / height;
            height = maxHeight;
          }
        }
        
        // Configurar canvas
        canvas.width = width;
        canvas.height = height;
        
        // Dibujar imagen redimensionada
        ctx.drawImage(img, 0, 0, width, height);
        
        // Convertir a base64 comprimido
        const compressedBase64 = canvas.toDataURL('image/jpeg', quality);
        resolve(compressedBase64);
      };
      
      img.src = URL.createObjectURL(file);
    });
  };

  // Función para manejar la captura de foto
  const handleTomarFoto = () => {
    if (fileInputRef.current) {
      // Configurar para captura directa de cámara
      fileInputRef.current.setAttribute('capture', 'environment');
      fileInputRef.current.click();
    }
  };

  // Función para manejar selección de galería
  const handleSeleccionarGaleria = () => {
    if (fileInputRef.current) {
      // Remover atributo capture para selección de galería
      fileInputRef.current.removeAttribute('capture');
      fileInputRef.current.click();
    }
  };

  // Función para manejar el archivo seleccionado/capturado
  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (file) {
      setTomaFoto(true);
      try {
        // Verificar que sea una imagen
        if (!file.type.startsWith('image/')) {
          setSnackbar({
            open: true,
            message: "Por favor selecciona un archivo de imagen válido",
            severity: 'error'
          });
          setTomaFoto(false);
          return;
        }

        // Verificar tamaño del archivo original (máximo 10MB)
        if (file.size > 10 * 1024 * 1024) {
          setSnackbar({
            open: true,
            message: "La imagen es demasiado grande. Máximo 10MB permitido",
            severity: 'error'
          });
          setTomaFoto(false);
          return;
        }

        // Comprimir imagen para reducir tamaño
        const compressedBase64 = await compressImage(file, 600, 600, 0.6);
        
        // Verificar que la imagen comprimida no sea demasiado grande para Firestore
        const sizeInBytes = Math.round((compressedBase64.length * 3) / 4);
        const maxFirestoreSize = 500000; // 500KB límite seguro para Firestore
        
        if (sizeInBytes > maxFirestoreSize) {
          // Comprimir más si aún es muy grande
          const moreCompressed = await compressImage(file, 400, 400, 0.4);
          const newSize = Math.round((moreCompressed.length * 3) / 4);
          
          if (newSize > maxFirestoreSize) {
            setSnackbar({
              open: true,
              message: "La imagen no se pudo comprimir lo suficiente. Intenta con una imagen más pequeña",
              severity: 'error'
            });
            setTomaFoto(false);
            return;
          }
          
          // Usar la imagen más comprimida
          setNuevoItem({
            ...nuevoItem,
            imagenURL: moreCompressed
          });
        } else {
          // Usar la imagen comprimida normal
          setNuevoItem({
            ...nuevoItem,
            imagenURL: compressedBase64
          });
        }

        setSnackbar({
          open: true,
          message: `Imagen capturada y optimizada correctamente (${Math.round(sizeInBytes / 1024)}KB)`,
          severity: 'success'
        });
      } catch (error) {
        console.error("Error al procesar la imagen:", error);
        setSnackbar({
          open: true,
          message: "Error al procesar la imagen: " + error.message,
          severity: 'error'
        });
      } finally {
        setTomaFoto(false);
        // Limpiar el input para permitir seleccionar la misma imagen nuevamente
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    }
  };

  return (
    <AdminLayout>
      <Container maxWidth="lg" sx={{ mt: 0, mb: 2, px: isSmallMobile ? 0.5 : 1 }}>
        <Paper sx={{ p: isSmallMobile ? 1 : 2, mb: 2, mx: isSmallMobile ? 0.5 : 0 }}>
          <Typography 
            variant={isSmallMobile ? "h6" : "h4"} 
            component="h1" 
            gutterBottom
            sx={{ 
              fontWeight: 'bold', 
              textAlign: 'center', 
              color: '#c46494',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: isSmallMobile ? 1 : 2,
              fontSize: isSmallMobile ? '1.1rem' : '2rem'
            }}
          >
            <Inventory sx={{ 
              mr: isSmallMobile ? 0.5 : 1, 
              color: '#602442',
              fontSize: isSmallMobile ? '1.25rem' : '2rem'
            }} /> 
            {isSmallMobile ? "Inventario" : "Gestión de Inventario "}
          </Typography>
          
          {/* Indicador de carga */}
          {cargando && (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
              <CircularProgress />
            </Box>
          )}
          
          {/* Sección de alertas para productos próximos a vencer */}
          {productosAlerta.length > 0 && (
            <Box sx={{ mb: isSmallMobile ? 1.5 : 3 }}>
              <Alert 
                severity="warning" 
                icon={<Warning fontSize={isSmallMobile ? "small" : "medium"} />}
                sx={{ 
                  mb: isSmallMobile ? 1 : 2,
                  fontSize: isSmallMobile ? '0.75rem' : '0.875rem'
                }}
              >
                <Typography 
                  variant={isSmallMobile ? "body2" : "subtitle1"} 
                  fontWeight="bold"
                  sx={{ fontSize: isSmallMobile ? '0.8rem' : '1rem' }}
                >
                  Hay {productosAlerta.length} producto(s) próximos a vencer
                </Typography>
              </Alert>
              <TableContainer component={Paper} variant="outlined" sx={{ mb: isSmallMobile ? 1.5 : 3 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontSize: isSmallMobile ? '0.75rem' : '0.875rem', py: isSmallMobile ? 0.5 : 1 }}>
                        Producto
                      </TableCell>
                      {!isSmallMobile && <TableCell sx={{ fontSize: '0.875rem' }}>Vencimiento</TableCell>}
                      <TableCell sx={{ fontSize: isSmallMobile ? '0.75rem' : '0.875rem', py: isSmallMobile ? 0.5 : 1 }}>
                        Días Restantes
                      </TableCell>
                      <TableCell align="center" sx={{ fontSize: isSmallMobile ? '0.75rem' : '0.875rem', py: isSmallMobile ? 0.5 : 1 }}>
                        Acción
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {productosAlerta.map((item) => {
                      const diasRestantes = calcularDiasHastaVencimiento(item.fechaVencimiento);
                      return (
                        <TableRow key={`alerta-${item.id}`}>
                          <TableCell>
                            <Typography variant="body2" sx={{ 
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              maxWidth: isSmallMobile ? 120 : 'none'
                            }}>
                              {item.nombre}
                            </Typography>
                            {isSmallMobile && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                {new Date(item.fechaVencimiento).toLocaleDateString()}
                              </Typography>
                            )}
                          </TableCell>
                          {!isSmallMobile && <TableCell>{new Date(item.fechaVencimiento).toLocaleDateString()}</TableCell>}
                          <TableCell>
                            <Chip 
                              label={`${diasRestantes} días`} 
                              color={diasRestantes <= 7 ? "error" : diasRestantes <= 15 ? "warning" : "default"}
                              size="small" 
                            />
                          </TableCell>
                          <TableCell align="center">
                            <IconButton size="small" color="primary" onClick={() => handleOpenDialog(item)}>
                              <Edit fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
          
          <Grid container spacing={isSmallMobile ? 1 : 2} alignItems="center" sx={{ mb: isSmallMobile ? 2 : 3 }}>
            <Grid item xs={12} md={8}>
              <TextField
                label="Buscar en inventario"
                variant="outlined"
                size="small"
                fullWidth
                value={busqueda}
                onChange={handleBusquedaChange}
                InputProps={{
                  startAdornment: <Search color="action" sx={{ mr: 0.5, fontSize: isSmallMobile ? '1rem' : '1.25rem' }} />,
                  sx: { 
                    borderRadius: 1,
                    fontSize: isSmallMobile ? '0.8rem' : '0.875rem',
                    height: isSmallMobile ? 36 : 40
                  }
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                    fontSize: isSmallMobile ? '0.8rem' : '0.875rem'
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: isSmallMobile ? '0.8rem' : '0.875rem'
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={4} sx={{ 
              display: 'flex', 
              justifyContent: { xs: 'center', md: 'flex-end' },
              alignItems: 'center',
              mt: { xs: 1, md: 0 }
            }}>
              <Button 
                variant="contained" 
                color="primary" 
                startIcon={<Add fontSize={isSmallMobile ? "small" : "medium"} />}
                onClick={() => handleOpenDialog()}
                size={isSmallMobile ? "small" : "large"}
                sx={{
                  backgroundColor: '#c46494',
                  '&:hover': {
                    backgroundColor: '#a0527a'
                  },
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 'bold',
                  minWidth: isSmallMobile ? 100 : 180,
                  fontSize: isSmallMobile ? '0.75rem' : '0.875rem',
                  py: isSmallMobile ? 0.75 : 1
                }}
              >
                {isSmallMobile ? "Añadir" : "Agregar Producto"}
              </Button>
            </Grid>
          </Grid>
          
          {/* Vista de tabla completamente responsive */}
          <TableContainer component={Paper} sx={{ 
            overflowX: 'hidden', 
            fontSize: isSmallMobile ? '0.7rem' : '0.875rem'
          }}>
            <Table aria-label="tabla de inventario" size="small">
              <TableHead>
                <TableRow>
                  {!isSmallMobile && <TableCell sx={{ width: 50, p: 0.5, fontSize: '0.75rem' }}>Img</TableCell>}
                  <TableCell sx={{ 
                    fontSize: isSmallMobile ? '0.75rem' : '0.875rem',
                    p: isSmallMobile ? 0.5 : 1,
                    fontWeight: 'bold',
                    width: isSmallMobile ? 'auto' : '40%'
                  }}>
                    Producto
                  </TableCell>
                  <TableCell align="right" sx={{ 
                    fontSize: isSmallMobile ? '0.75rem' : '0.875rem',
                    p: isSmallMobile ? 0.5 : 1,
                    fontWeight: 'bold',
                    width: isSmallMobile ? '15%' : '10%',
                    minWidth: isSmallMobile ? 45 : 60
                  }}>
                    {isSmallMobile ? 'Stock' : 'Stock'}
                  </TableCell>
                  <TableCell align="right" sx={{ 
                    fontSize: isSmallMobile ? '0.75rem' : '0.875rem',
                    p: isSmallMobile ? 0.5 : 1,
                    fontWeight: 'bold',
                    width: isSmallMobile ? '25%' : '20%',
                    minWidth: isSmallMobile ? 85 : 100
                  }}>
                    Precio
                  </TableCell>
                  <TableCell align="center" sx={{ 
                    width: isSmallMobile ? '20%' : '15%',
                    fontSize: isSmallMobile ? '0.75rem' : '0.875rem',
                    p: isSmallMobile ? 0.5 : 1,
                    fontWeight: 'bold',
                    minWidth: isSmallMobile ? 70 : 80
                  }}>
                    Acciones
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {inventarioFiltrado.map((item) => {
                  const diasRestantes = item.fechaVencimiento ? calcularDiasHastaVencimiento(item.fechaVencimiento) : null;
                  return (
                    <TableRow key={item.id} hover sx={{ height: isSmallMobile ? 'auto' : 'inherit' }}>
                      {!isSmallMobile && (
                        <TableCell sx={{ p: 0.25 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                            {item.imagenURL ? (
                              <Box
                                component="img"
                                sx={{
                                  width: 32,
                                  height: 32,
                                  objectFit: 'contain',
                                  borderRadius: 1,
                                  border: '1px solid #e0e0e0',
                                  p: 0.25,
                                  bgcolor: '#f8f8f8',
                                  cursor: 'pointer'
                                }}
                                src={item.imagenURL}
                                alt={item.nombre}
                                loading="lazy"
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.style.display = 'none';
                                }}
                                onClick={() => {
                                  const modal = document.createElement('div');
                                  modal.style.cssText = `
                                    position: fixed; top: 0; left: 0; width: 100%; height: 100%; 
                                    background: rgba(0,0,0,0.8); display: flex; justify-content: center; 
                                    align-items: center; z-index: 9999; cursor: pointer;
                                  `;
                                  const img = document.createElement('img');
                                  img.src = item.imagenURL;
                                  img.style.cssText = 'max-width: 90%; max-height: 90%; border-radius: 8px;';
                                  modal.appendChild(img);
                                  modal.onclick = () => document.body.removeChild(modal);
                                  document.body.appendChild(modal);
                                }}
                              />
                            ) : (
                              <Box
                                sx={{
                                  width: 32,
                                  height: 32,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  backgroundColor: '#f0f0f0',
                                  borderRadius: 1,
                                  border: '1px solid #e0e0e0'
                                }}
                              >
                                <ImageNotSupported sx={{ color: '#bdbdbd', fontSize: 16 }} />
                              </Box>
                            )}
                          </Box>
                        </TableCell>
                      )}
                      <TableCell component="th" scope="row" sx={{ 
                        width: isSmallMobile ? 'auto' : '40%',
                        minWidth: 0, 
                        p: isSmallMobile ? 0.75 : 1,
                        maxWidth: isSmallMobile ? 140 : 'none'
                      }}>
                        <Box>
                          <Typography 
                            variant={isSmallMobile ? "body2" : "subtitle2"} 
                            fontWeight="bold" 
                            sx={{ 
                              mb: 0.25,
                              fontSize: isSmallMobile ? '0.8rem' : '0.9rem',
                              lineHeight: 1.2,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: isSmallMobile ? 'normal' : 'nowrap',
                              display: '-webkit-box',
                              WebkitLineClamp: isSmallMobile ? 2 : 1,
                              WebkitBoxOrient: 'vertical',
                              wordBreak: 'break-word'
                            }}
                          >
                            {item.nombre}
                          </Typography>
                          
                          {/* Información secundaria optimizada para móviles */}
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.2 }}>
                            {item.categoria && (
                              <Typography 
                                variant="caption" 
                                color="text.secondary" 
                                sx={{ 
                                  fontSize: isSmallMobile ? '0.65rem' : '0.75rem',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  maxWidth: isSmallMobile ? 120 : 'none'
                                }}
                              >
                                🏷️ {item.categoria}
                              </Typography>
                            )}
                            {item.presentacion && (
                              <Typography 
                                variant="caption" 
                                color="text.secondary" 
                                sx={{ 
                                  fontSize: isSmallMobile ? '0.65rem' : '0.75rem',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  maxWidth: isSmallMobile ? 120 : 'none'
                                }}
                              >
                                💊 {item.presentacion}
                              </Typography>
                            )}
                            {item.fechaVencimiento && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                                <Typography 
                                  variant="caption" 
                                  color="text.secondary"
                                  sx={{ fontSize: isSmallMobile ? '0.55rem' : '0.7rem' }}
                                >
                                  📅 {new Date(item.fechaVencimiento).toLocaleDateString()}
                                </Typography>
                                {diasRestantes <= 30 && (
                                  <Chip 
                                    size="small" 
                                    label={`${diasRestantes}d`}
                                    color={diasRestantes <= 7 ? "error" : diasRestantes <= 15 ? "warning" : "default"}
                                    sx={{ 
                                      fontSize: isSmallMobile ? '0.55rem' : '0.65rem', 
                                      height: isSmallMobile ? 14 : 18,
                                      '& .MuiChip-label': {
                                        px: 0.5
                                      }
                                    }}
                                  />
                                )}
                              </Box>
                            )}
                            {/* En móviles pequeños, mostrar imagen como emoji si hay URL */}
                            {isSmallMobile && item.imagenURL && (
                              <Typography 
                                variant="caption" 
                                color="primary" 
                                sx={{ 
                                  cursor: 'pointer',
                                  fontSize: '0.6rem'
                                }}
                                onClick={() => {
                                  const modal = document.createElement('div');
                                  modal.style.cssText = `
                                    position: fixed; top: 0; left: 0; width: 100%; height: 100%; 
                                    background: rgba(0,0,0,0.8); display: flex; justify-content: center; 
                                    align-items: center; z-index: 9999; cursor: pointer;
                                  `;
                                  const img = document.createElement('img');
                                  img.src = item.imagenURL;
                                  img.style.cssText = 'max-width: 90%; max-height: 90%; border-radius: 8px;';
                                  modal.appendChild(img);
                                  modal.onclick = () => document.body.removeChild(modal);
                                  document.body.appendChild(modal);
                                }}
                              >
                                🖼️ Ver
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                      
                      <TableCell align="right" sx={{ 
                        width: isSmallMobile ? '15%' : '10%',
                        minWidth: isSmallMobile ? 45 : 60,
                        p: isSmallMobile ? 0.5 : 1
                      }}>
                        <Typography 
                          variant={isSmallMobile ? "body2" : "body1"} 
                          fontWeight="bold" 
                          color={item.cantidad === 0 ? 'error' : 'text.primary'}
                          sx={{ 
                            fontSize: isSmallMobile ? '0.8rem' : '0.875rem',
                            textAlign: 'center'
                          }}
                        >
                          {item.cantidad}
                        </Typography>
                        {isSmallMobile && item.cantidad === 0 && (
                          <Typography 
                            variant="caption" 
                            color="error" 
                            display="block"
                            sx={{ fontSize: '0.65rem', textAlign: 'center' }}
                          >
                            Agotado
                          </Typography>
                        )}
                      </TableCell>
                      
                      <TableCell align="right" sx={{ 
                        width: isSmallMobile ? '25%' : '20%',
                        minWidth: isSmallMobile ? 85 : 100,
                        p: isSmallMobile ? 0.75 : 1,
                        maxWidth: isSmallMobile ? 110 : 'none'
                      }}>
                        <Box sx={{ 
                          display: 'flex', 
                          flexDirection: 'column', 
                          alignItems: 'flex-end',
                          gap: isSmallMobile ? 0.25 : 0.5
                        }}>
                          {/* Precio de Venta */}
                          <Typography 
                            variant={isSmallMobile ? "body2" : "body1"} 
                            fontWeight="bold" 
                            color="primary"
                            sx={{ 
                              fontSize: isSmallMobile ? '0.8rem' : '0.875rem',
                              lineHeight: 1.2,
                              wordBreak: 'keep-all',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {item.precioVenta && item.precioVenta > 0 ? 
                              `$${Number(item.precioVenta).toLocaleString('es-CO', { maximumFractionDigits: 0 })}` : 
                              'Sin precio'
                            }
                          </Typography>
                          
                          {/* Precio de Compra - Mostrar siempre, no solo en móvil */}
                          {item.precio && item.precio > 0 && (
                            <Typography 
                              variant="caption" 
                              color="text.secondary" 
                              sx={{ 
                                fontSize: isSmallMobile ? '0.65rem' : '0.7rem',
                                lineHeight: 1,
                                whiteSpace: 'nowrap'
                              }}
                            >
                              Costo: ${Number(item.precio).toLocaleString('es-CO', { maximumFractionDigits: 0 })}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      
                      <TableCell align="center" sx={{ 
                        width: isSmallMobile ? '20%' : '15%',
                        minWidth: isSmallMobile ? 70 : 80, 
                        p: isSmallMobile ? 0.5 : 0.5 
                      }}>
                        <Box sx={{ 
                          display: 'flex', 
                          flexDirection: isSmallMobile ? 'column' : 'row', 
                          gap: isSmallMobile ? 0.25 : 0.5,
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <IconButton 
                            color="primary" 
                            onClick={() => handleOpenDialog(item)}
                            size="small"
                            sx={{ 
                              minWidth: 'auto', 
                              p: isSmallMobile ? 0.25 : 0.5,
                              '& .MuiSvgIcon-root': {
                                fontSize: isSmallMobile ? '0.9rem' : '1.25rem'
                              }
                            }}
                          >
                            <Edit />
                          </IconButton>
                          <IconButton 
                            color="error" 
                            onClick={() => handleEliminarItem(item.id)}
                            size="small"
                            sx={{ 
                              minWidth: 'auto', 
                              p: isSmallMobile ? 0.25 : 0.5,
                              '& .MuiSvgIcon-root': {
                                fontSize: isSmallMobile ? '0.9rem' : '1.25rem'
                              }
                            }}
                          >
                            <Delete />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {inventarioFiltrado.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={isSmallMobile ? 4 : 5} align="center" sx={{ 
                      py: isSmallMobile ? 2 : 4,
                      px: isSmallMobile ? 1 : 2
                    }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                        <Inventory sx={{ 
                          fontSize: isSmallMobile ? 32 : 48, 
                          color: 'text.disabled' 
                        }} />
                        <Typography 
                          variant={isSmallMobile ? "body1" : "h6"} 
                          color="text.secondary"
                          sx={{ fontSize: isSmallMobile ? '0.9rem' : '1.25rem' }}
                        >
                          No se encontraron productos
                        </Typography>
                        <Typography 
                          variant="body2" 
                          color="text.secondary"
                          sx={{ 
                            fontSize: isSmallMobile ? '0.75rem' : '0.875rem',
                            textAlign: 'center',
                            px: 1
                          }}
                        >
                          {busqueda ? 'Intenta ajustar la búsqueda' : 'Agrega tu primer producto al inventario'}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
        
        {/* Diálogo para agregar/editar producto */}
        <Dialog 
          open={openDialog} 
          onClose={handleCloseDialog} 
          maxWidth="md"
          fullWidth
          fullScreen={isSmallMobile}
          PaperProps={{
            sx: {
              ...(isSmallMobile && {
                m: 0,
                borderRadius: 0,
                maxHeight: '100vh'
              }),
              ...(!isSmallMobile && {
                borderRadius: 2,
                minHeight: '60vh'
              })
            }
          }}
        >
          <DialogTitle sx={{
            backgroundColor: '#c46494',
            color: 'white',
            fontWeight: 'bold',
            fontSize: isSmallMobile ? '1.1rem' : '1.25rem',
            py: isSmallMobile ? 1.5 : 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Inventory sx={{ mr: 1, fontSize: isSmallMobile ? '1.2rem' : '1.5rem' }} />
              {itemSeleccionado ? 'Editar Producto' : 'Agregar Nuevo Producto'}
            </Box>
          </DialogTitle>
          <DialogContent sx={{ 
            pb: isSmallMobile ? 10 : 2,
            px: isSmallMobile ? 2 : 3,
            pt: isSmallMobile ? 2 : 3,
            maxHeight: isSmallMobile ? 'calc(100vh - 120px)' : 'none',
            overflowY: 'auto'
          }}>
            <Grid container spacing={isSmallMobile ? 2 : 3}>
              <Grid item xs={12} md={6}>
                <TextField
                  margin={isSmallMobile ? "normal" : "dense"}
                  name="nombre"
                  label="Nombre del producto"
                  type="text"
                  fullWidth
                  variant="outlined"
                  value={nuevoItem.nombre}
                  onChange={handleInputChange}
                  size={isSmallMobile ? "medium" : "small"}
                  required
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1.5
                    }
                  }}
                />
                <TextField
                  margin={isSmallMobile ? "normal" : "dense"}
                  name="categoria"
                  label="Categoría"
                  type="text"
                  fullWidth
                  variant="outlined"
                  value={nuevoItem.categoria}
                  onChange={handleInputChange}
                  size={isSmallMobile ? "medium" : "small"}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1.5
                    }
                  }}
                />
                <TextField
                  margin={isSmallMobile ? "normal" : "dense"}
                  name="presentacion"
                  label="Presentación"
                  placeholder="Ej: Tabletas, Cápsula, Jarabe"
                  type="text"
                  fullWidth
                  variant="outlined"
                  value={nuevoItem.presentacion || ''}
                  onChange={handleInputChange}
                  size={isSmallMobile ? "medium" : "small"}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1.5
                    }
                  }}
                />
                <TextField
                  margin={isSmallMobile ? "normal" : "dense"}
                  name="cantidad"
                  label="Cantidad en Stock"
                  type="number"
                  fullWidth
                  variant="outlined"
                  value={nuevoItem.cantidad}
                  onChange={handleInputChange}
                  size={isSmallMobile ? "medium" : "small"}
                  required
                  InputProps={{
                    inputProps: { 
                      min: 0,
                      step: '1',
                      onWheel: (e) => e.target.blur(),
                      onTouchMove: (e) => e.preventDefault()
                    }
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1.5
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  margin={isSmallMobile ? "normal" : "dense"}
                  name="precio"
                  label="Precio de Compra (COP)"
                  type="number"
                  fullWidth
                  variant="outlined"
                  value={nuevoItem.precio}
                  onChange={handleInputChange}
                  size={isSmallMobile ? "medium" : "small"}
                  InputProps={{
                    inputProps: { 
                      min: 0,
                      step: 'any',
                      onWheel: (e) => e.target.blur(),
                      onTouchMove: (e) => e.preventDefault()
                    },
                    startAdornment: <Typography variant="body2" sx={{ mr: 1, color: 'text.secondary' }}>$</Typography>
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1.5
                    }
                  }}
                />
                <TextField
                  margin={isSmallMobile ? "normal" : "dense"}
                  name="precioVenta"
                  label="Precio de Venta (COP)"
                  type="number"
                  fullWidth
                  variant="outlined"
                  value={nuevoItem.precioVenta}
                  onChange={handleInputChange}
                  size={isSmallMobile ? "medium" : "small"}
                  required
                  InputProps={{
                    inputProps: { 
                      min: 0,
                      step: 'any',
                      onWheel: (e) => e.target.blur(),
                      onTouchMove: (e) => e.preventDefault()
                    },
                    startAdornment: <Typography variant="body2" sx={{ mr: 1, color: 'text.secondary' }}>$</Typography>
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1.5
                    }
                  }}
                />
                <TextField
                  margin={isSmallMobile ? "normal" : "dense"}
                  name="fechaVencimiento"
                  label="Fecha de Vencimiento"
                  type="date"
                  fullWidth
                  variant="outlined"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  value={nuevoItem.fechaVencimiento || ''}
                  onChange={handleInputChange}
                  size={isSmallMobile ? "medium" : "small"}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1.5
                    }
                  }}
                />
                <TextField
                  margin={isSmallMobile ? "normal" : "dense"}
                  name="proveedor"
                  label="Proveedor"
                  type="text"
                  fullWidth
                  variant="outlined"
                  value={nuevoItem.proveedor}
                  onChange={handleInputChange}
                  size={isSmallMobile ? "medium" : "small"}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1.5
                    }
                  }}
                />
              </Grid>
            </Grid>
            
            {/* Sección de Imagen */}
            <Box sx={{ 
              mt: 3, 
              border: '1px solid #e0e0e0', 
              borderRadius: 2, 
              p: isSmallMobile ? 2 : 3,
              backgroundColor: 'rgba(196, 100, 148, 0.02)'
            }}>
              <Typography variant={isSmallMobile ? "subtitle2" : "subtitle1"} gutterBottom sx={{ 
                fontWeight: 'bold',
                color: '#602442',
                display: 'flex',
                alignItems: 'center'
              }}>
                <ImageNotSupported sx={{ mr: 1, fontSize: isSmallMobile ? '1.1rem' : '1.25rem' }} />
                Imagen del Producto
              </Typography>
              
              {/* Botones de cámara y galería para móviles */}
              {(isMobile || isSmallMobile) && (
                <Box sx={{ 
                  display: 'flex', 
                  gap: 1, 
                  mb: 2,
                  justifyContent: 'center',
                  flexWrap: 'wrap'
                }}>
                  <Button
                    variant="outlined"
                    startIcon={tomaFoto ? <CircularProgress size={16} /> : <CameraAlt />}
                    onClick={handleTomarFoto}
                    disabled={tomaFoto}
                    size={isSmallMobile ? "small" : "medium"}
                    sx={{
                      borderColor: '#c46494',
                      color: '#c46494',
                      '&:hover': {
                        borderColor: '#a0527a',
                        backgroundColor: 'rgba(196, 100, 148, 0.04)'
                      },
                      textTransform: 'none',
                      fontWeight: 'bold',
                      borderRadius: 2,
                      minWidth: isSmallMobile ? 120 : 140
                    }}
                  >
                    {tomaFoto ? 'Procesando...' : 'Tomar Foto'}
                  </Button>
                  
                  <Button
                    variant="outlined"
                    startIcon={<PhotoLibrary />}
                    onClick={handleSeleccionarGaleria}
                    size={isSmallMobile ? "small" : "medium"}
                    sx={{
                      borderColor: '#c46494',
                      color: '#c46494',
                      '&:hover': {
                        borderColor: '#a0527a',
                        backgroundColor: 'rgba(196, 100, 148, 0.04)'
                      },
                      textTransform: 'none',
                      fontWeight: 'bold',
                      borderRadius: 2,
                      minWidth: isSmallMobile ? 120 : 140
                    }}
                  >
                    Galería
                  </Button>
                </Box>
              )}

              {/* Input oculto para captura de imagen */}
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                accept="image/*"
                capture="environment" // Usa la cámara trasera por defecto
                style={{ display: 'none' }}
              />
              
              {/* Campo para URL de imagen */}
              <TextField
                margin={isSmallMobile ? "normal" : "dense"}
                name="imagenURL"
                label="URL de la imagen (opcional)"
                type="url"
                fullWidth
                variant="outlined"
                value={nuevoItem.imagenURL || ''}
                onChange={handleInputChange}
                helperText={
                  (isMobile || isSmallMobile) 
                    ? "Usa los botones de arriba para tomar una foto o seleccionar de galería, o ingresa una URL"
                    : "Ingrese la URL de una imagen del producto"
                }
                size={isSmallMobile ? "medium" : "small"}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1.5,
                    backgroundColor: 'white'
                  }
                }}
              />
              
              {/* Vista previa de la imagen */}
              <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                {nuevoItem.imagenURL ? (
                  <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Box
                      component="img"
                      sx={{
                        height: isSmallMobile ? 120 : 160,
                        maxWidth: '100%',
                        objectFit: 'contain',
                        border: '2px solid #e0e0e0',
                        borderRadius: 2,
                        backgroundColor: 'white',
                        p: 1
                      }}
                      src={nuevoItem.imagenURL}
                      alt="Vista previa"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.style.display = 'none';
                      }}
                    />
                    {/* Mostrar tamaño de la imagen si es base64 */}
                    {nuevoItem.imagenURL.startsWith('data:') && (
                      <Typography 
                        variant="caption" 
                        color="text.secondary"
                        sx={{ 
                          mt: 0.5, 
                          fontSize: '0.7rem',
                          backgroundColor: 'rgba(0,0,0,0.04)',
                          px: 1,
                          py: 0.25,
                          borderRadius: 1
                        }}
                      >
                        Tamaño: {Math.round((nuevoItem.imagenURL.length * 3) / 4 / 1024)}KB
                      </Typography>
                    )}
                    {/* Botón para eliminar imagen */}
                    <IconButton
                      size="small"
                      onClick={() => setNuevoItem({ ...nuevoItem, imagenURL: '' })}
                      sx={{
                        position: 'absolute',
                        top: -8,
                        right: -8,
                        backgroundColor: '#d32f2f',
                        color: 'white',
                        '&:hover': {
                          backgroundColor: '#b71c1c'
                        },
                        width: 24,
                        height: 24
                      }}
                    >
                      <Delete sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Box>
                ) : (
                  <Box 
                    sx={{
                      height: isSmallMobile ? 120 : 160,
                      width: '100%',
                      maxWidth: 300,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '2px dashed #ccc',
                      borderRadius: 2,
                      color: 'text.secondary',
                      p: 2,
                      backgroundColor: 'rgba(255, 255, 255, 0.5)'
                    }}
                  >
                    <ImageNotSupported sx={{ fontSize: isSmallMobile ? 32 : 40, mb: 1, color: '#bdbdbd' }} />
                    <Typography variant="body2" align="center" sx={{ fontSize: isSmallMobile ? '0.8rem' : '0.875rem' }}>
                      {(isMobile || isSmallMobile) 
                        ? "Toma una foto o selecciona una imagen" 
                        : "Ingrese una URL para ver la vista previa"
                      }
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </DialogContent>
          <DialogActions sx={{ 
            position: isSmallMobile ? 'fixed' : 'static',
            bottom: isSmallMobile ? 0 : 'auto',
            left: isSmallMobile ? 0 : 'auto',
            right: isSmallMobile ? 0 : 'auto',
            bgcolor: isSmallMobile ? 'background.paper' : 'transparent',
            borderTop: isSmallMobile ? '1px solid' : 0,
            borderColor: 'divider',
            p: isSmallMobile ? 2.5 : 1.5,
            zIndex: 2,
            gap: 1,
            justifyContent: isSmallMobile ? 'space-between' : 'flex-end',
            boxShadow: isSmallMobile ? '0 -2px 8px rgba(0,0,0,0.1)' : 'none'
          }}>
            <Button 
              onClick={handleCloseDialog}
              variant={isSmallMobile ? "outlined" : "text"}
              color="inherit"
              size={isSmallMobile ? "large" : "medium"}
              sx={{ 
                width: isSmallMobile ? '45%' : 'auto',
                textTransform: 'none',
                fontWeight: 'bold',
                borderRadius: 2,
                ...(isSmallMobile && {
                  borderColor: '#ccc',
                  '&:hover': {
                    borderColor: '#999'
                  }
                })
              }}
            >
              Cancelar
            </Button>
            <Button 
              onClick={handleGuardarItem} 
              variant="contained"
              size={isSmallMobile ? "large" : "medium"}
              disabled={guardando}
              startIcon={guardando ? <CircularProgress size={16} color="inherit" /> : null}
              sx={{ 
                width: isSmallMobile ? '45%' : 'auto',
                backgroundColor: '#c46494',
                '&:hover': {
                  backgroundColor: '#a0527a'
                },
                '&:disabled': {
                  backgroundColor: '#ccc',
                  color: 'rgba(255, 255, 255, 0.7)'
                },
                textTransform: 'none',
                fontWeight: 'bold',
                borderRadius: 2
              }}
            >
              {guardando ? 'Guardando...' : (itemSeleccionado ? 'Actualizar' : 'Guardar')}
            </Button>
          </DialogActions>
        </Dialog>
        
        {/* Snackbar para notificaciones */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ 
            vertical: 'top', 
            horizontal: 'center' 
          }}
          sx={{
            mt: 10, // Margen superior para que aparezca debajo de la barra de navegación
            '& .MuiSnackbarContent-root': {
              backgroundColor: snackbar.severity === 'error' ? '#d32f2f' : 
                             snackbar.severity === 'success' ? '#2e7d32' : 
                             snackbar.severity === 'warning' ? '#ed6c02' : '#0288d1',
              color: 'white',
              fontSize: isSmallMobile ? '0.875rem' : '1rem',
              borderRadius: 2,
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              maxWidth: isSmallMobile ? '90vw' : '500px'
            },
            '& .MuiSnackbarContent-message': {
              padding: isSmallMobile ? '4px 0' : '8px 0'
            }
          }}
        >
          <Alert 
            severity={snackbar.severity}
            onClose={handleCloseSnackbar}
            variant="filled"
            sx={{
              width: '100%',
              fontSize: isSmallMobile ? '0.875rem' : '1rem',
              '& .MuiAlert-icon': {
                fontSize: isSmallMobile ? '1.1rem' : '1.25rem'
              }
            }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>

        {/* Diálogo de confirmación para eliminar */}
        <Dialog
          open={confirmDelete.open}
          onClose={handleCancelDelete}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              p: 1
            }
          }}
        >
          <DialogTitle sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            color: '#d32f2f',
            fontWeight: 'bold',
            pb: 1
          }}>
            <Delete sx={{ color: '#d32f2f' }} />
            Confirmar Eliminación
          </DialogTitle>
          <DialogContent>
            <Typography variant="body1" sx={{ mb: 2 }}>
              ¿Estás seguro que deseas eliminar este producto del inventario?
            </Typography>
            {confirmDelete.item && (
              <Box sx={{ 
                p: 2, 
                bgcolor: 'rgba(211, 47, 47, 0.04)', 
                borderRadius: 2,
                border: '1px solid rgba(211, 47, 47, 0.2)'
              }}>
                <Typography variant="subtitle1" fontWeight="bold" color="text.primary">
                  📦 {confirmDelete.item.nombre}
                </Typography>
                {confirmDelete.item.categoria && (
                  <Typography variant="body2" color="text.secondary">
                    🏷️ Categoría: {confirmDelete.item.categoria}
                  </Typography>
                )}
                {confirmDelete.item.cantidad !== undefined && (
                  <Typography variant="body2" color="text.secondary">
                    📊 Stock actual: {confirmDelete.item.cantidad} unidades
                  </Typography>
                )}
              </Box>
            )}
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Esta acción no se puede deshacer.</strong> El producto será eliminado permanentemente del inventario.
              </Typography>
            </Alert>
          </DialogContent>
          <DialogActions sx={{ p: 2, gap: 1 }}>
            <Button 
              onClick={handleCancelDelete}
              variant="outlined"
              sx={{ 
                textTransform: 'none',
                fontWeight: 'bold',
                borderRadius: 2,
                minWidth: 100
              }}
            >
              Cancelar
            </Button>
            <Button 
              onClick={handleConfirmDelete}
              variant="contained"
              color="error"
              sx={{ 
                textTransform: 'none',
                fontWeight: 'bold',
                borderRadius: 2,
                minWidth: 100
              }}
            >
              Eliminar
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </AdminLayout>
  );
};

export default Inventario;
